import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChartLineUp, ShieldCheck, Users, ArrowRight, MapPin, Clock, CheckCircle } from "@phosphor-icons/react";
import aakritiLogo from "@/assets/aakriti-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PROPERTY_TYPES } from "@/lib/constants";

const HERO = "https://images.pexels.com/photos/30950764/pexels-photo-30950764.jpeg";
const IMG_PROCESS = "https://images.pexels.com/photos/20208884/pexels-photo-20208884.jpeg";
const IMG_TEAM = "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg";

export default function Landing() {
  const [form, setForm] = useState({
    bank_name: "", branch_name: "", contact_person: "", phone: "", email: "",
    applicant_name: "", property_type: "Residential", property_address: "", loan_amount: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, loan_amount: parseFloat(form.loan_amount) || 0 };
      const { data } = await api.post("/public/intake", payload);
      setDone(data.reference);
      toast.success("Valuation request submitted");
      setForm({ bank_name: "", branch_name: "", contact_person: "", phone: "", email: "", applicant_name: "", property_type: "Residential", property_address: "", loan_amount: "", notes: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="brand-logo">
            <div className="h-10 rounded-md bg-white border border-border flex items-center justify-center px-1.5">
              <img src={aakritiLogo} alt="Aakriti" className="h-7 w-auto object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-primary text-lg">Aakriti Associates</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Valuation Professionals</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="#request" className="hidden sm:inline text-sm font-medium text-muted-foreground hover:text-primary transition-colors">Request Valuation</a>
            <Link to="/track" className="hidden sm:inline text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="nav-track">Track Request</Link>
            <Link to="/login">
              <Button data-testid="nav-login-btn" className="rounded-full bg-primary hover:bg-[#113A64] text-primary-foreground">
                Team Login <ArrowRight className="ml-1" size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-end noise-overlay">
        <img src={HERO} alt="Skyline" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/70 to-[#0A2540]/30" />
        <div className="relative max-w-7xl mx-auto px-6 pb-20 pt-32 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 border border-accent/40 text-accent text-xs tracking-[0.2em] uppercase font-bold mb-6">
              <MapPin size={14} weight="fill" /> Raipur, Chhattisgarh
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-none tracking-tight">
              Trusted Property Valuation for India's Leading Banks
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">
              Bank-compliant valuation reports delivered with precision and speed — serving 150+ bank branches across Raipur and Chhattisgarh.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#request"><Button data-testid="hero-request-btn" size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">Request a Valuation</Button></a>
              <a href="#services"><Button data-testid="hero-services-btn" size="lg" variant="outline" className="rounded-full border-white/40 bg-white/5 text-white hover:bg-white/15">Our Services</Button></a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
          {[["150+", "Bank Branches"], ["12+", "Partner Banks"], ["48h", "Avg. Turnaround"], ["100%", "Compliance"]].map(([n, l], i) => (
            <div key={l} className="py-8 px-4 text-center">
              <div className="font-display font-black text-3xl md:text-4xl text-primary tabular">{n}</div>
              <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services bento */}
      <section id="services" className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">What We Do</div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl text-primary mt-3">Precision valuation, end to end</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-12">
          <div className="md:col-span-8 rounded-xl overflow-hidden border border-border group relative min-h-[300px]">
            <img src={IMG_PROCESS} alt="Valuation reporting" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/90 to-transparent" />
            <div className="relative p-8 h-full flex flex-col justify-end">
              <ChartLineUp size={36} weight="duotone" className="text-accent mb-3" />
              <h3 className="font-display text-2xl font-semibold text-white">Accurate Report Writing</h3>
              <p className="text-white/75 mt-2 max-w-md">Detailed, defensible valuation reports formatted to each bank's empanelment standards.</p>
            </div>
          </div>
          <div className="md:col-span-4 grid gap-6">
            {[
              [ShieldCheck, "Bank Compliant", "RBI & bank-approved formats, fully audit-ready."],
              [Clock, "Fast Turnaround", "Site visit to submission in as little as 48 hours."],
            ].map(([Icon, t, d], i) => (
              <div key={t} className="rounded-xl border border-border bg-white p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
                <Icon size={30} weight="duotone" className="text-accent" />
                <h3 className="font-display text-xl font-semibold text-primary mt-3">{t}</h3>
                <p className="text-sm text-muted-foreground mt-1">{d}</p>
              </div>
            ))}
          </div>
          <div className="md:col-span-5 rounded-xl border border-border bg-primary text-white p-8 noise-overlay">
            <Users size={32} weight="duotone" className="text-accent" />
            <h3 className="font-display text-2xl font-semibold mt-3">Coordinated Field Team</h3>
            <p className="text-white/70 mt-2">Dedicated field executives and report writers work in sync so no job misses its deadline.</p>
          </div>
          <div className="md:col-span-7 rounded-xl overflow-hidden border border-border relative min-h-[220px] group">
            <img src={IMG_TEAM} alt="Team" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            <div className="absolute inset-0 bg-[#0A2540]/40" />
          </div>
        </div>
      </section>

      {/* Request form */}
      <section id="request" className="bg-[#0B0C10] text-white py-20 md:py-28 noise-overlay">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">Get Started</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl mt-3">Request a Valuation</h2>
            <p className="text-white/70 mt-4 leading-relaxed">Bank officials can submit a property valuation request directly. You'll receive a reference number and our team will pick it up right away.</p>
            <ul className="mt-8 space-y-3">
              {["No account needed", "Instant reference number", "Assigned to a field executive within hours"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-white/80"><CheckCircle weight="fill" className="text-accent" size={18} />{t}</li>
              ))}
            </ul>
          </div>
          {done ? (
            <div className="rounded-xl bg-white/5 border border-white/15 p-8 backdrop-blur-md" data-testid="intake-success">
              <CheckCircle weight="fill" size={44} className="text-accent" />
              <h3 className="font-display text-2xl mt-4">Request Received</h3>
              <p className="text-white/70 mt-2">Your reference number is</p>
              <div className="font-mono text-2xl text-accent mt-1 tracking-wide">{done}</div>
              <p className="text-white/60 text-sm mt-3">Save this number to track your request status anytime.</p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Link to={`/track`}><Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" data-testid="intake-track-btn">Track this request</Button></Link>
                <Button variant="outline" className="rounded-full border-white/40 bg-white/5 text-white hover:bg-white/15" onClick={() => setDone(null)} data-testid="intake-another-btn">Submit another</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-xl bg-white/5 border border-white/15 p-6 backdrop-blur-md space-y-4" data-testid="intake-form">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Bank Name *"><Input required data-testid="intake-bank" value={form.bank_name} onChange={set("bank_name")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" placeholder="e.g. SBI" /></Field>
                <Field label="Branch"><Input data-testid="intake-branch" value={form.branch_name} onChange={set("branch_name")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" placeholder="Branch" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Contact Person"><Input value={form.contact_person} onChange={set("contact_person")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" /></Field>
                <Field label="Phone *"><Input required data-testid="intake-phone" value={form.phone} onChange={set("phone")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" /></Field>
              </div>
              <Field label="Applicant Name *"><Input required data-testid="intake-applicant" value={form.applicant_name} onChange={set("applicant_name")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Property Type">
                  <Select value={form.property_type} onValueChange={set("property_type")}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{PROPERTY_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Loan Amount (₹)"><Input type="number" value={form.loan_amount} onChange={set("loan_amount")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" /></Field>
              </div>
              <Field label="Property Address *"><Textarea required data-testid="intake-address" value={form.property_address} onChange={set("property_address")} className="bg-white/10 border-white/20 text-white placeholder:text-white/40" rows={2} /></Field>
              <Button type="submit" disabled={submitting} data-testid="intake-submit" className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">{submitting ? "Submitting…" : "Submit Request"}</Button>
            </form>
          )}
        </div>
      </section>

      <footer className="bg-primary text-white/60 py-8 text-center text-sm">
        <div className="inline-flex items-center justify-center h-10 rounded-md bg-white px-2 mb-2"><img src={aakritiLogo} alt="Aakriti" className="h-7 w-auto object-contain" /></div>
        <div className="font-display text-white text-lg">Aakriti Associates</div>
        <p className="mt-1">Valuation Professionals · Raipur, Chhattisgarh · © 2026</p>
      </footer>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs tracking-wide uppercase text-white/60">{label}</Label>
      {children}
    </div>
  );
}
